import { Head, Link, router } from "@inertiajs/react"
import { Badge, Button, Card, Flex, Heading, Table } from "@radix-ui/themes"
import { Plus, Trash2 } from "lucide-react"
import { z } from "zod"

import LinkButton from "@/components/link-button"
import ProjectLayout from "@/layouts/project-layout"
import { newProjectListPath, projectDashboardPath, projectListPath } from "@/routes"
import { ProjectSchema } from "@/schemas"

const ListSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullish(),
  created_at: z.string(),
  count_customers: z.number(),
})

const PropsSchema = z.object({
  project: ProjectSchema,
  lists: z.array(ListSchema),
})

type Props = z.infer<typeof PropsSchema>

export default function ListsIndex(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project, lists } = validatedProps

  const handleDeleteList = (listId: number, listName: string) => {
    if (
      confirm(
        `Are you sure you want to delete the list "${listName}"? This action cannot be undone.`,
      )
    ) {
      router.delete(projectListPath(project.slug, listId))
    }
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Custom Lists" },
      ]}
    >
      <Head title="Custom Lists" />

      <Flex direction="column" gap="6" p="6">
        <Flex justify="between" align="center">
          <Heading size="6" weight="medium">
            Custom Lists ({lists.length})
          </Heading>

          <LinkButton href={newProjectListPath(project.slug)} variant="solid">
            <Plus className="h-4 w-4" />
            <span>New list</span>
          </LinkButton>
        </Flex>

        <Card>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Description</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Customers</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {lists.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5} style={{ textAlign: "center", color: "var(--gray-11)" }}>
                    No lists found
                  </Table.Cell>
                </Table.Row>
              ) : (
                lists.map((list) => (
                  <Table.Row key={list.id}>
                    <Table.RowHeaderCell>
                      <Link href={projectListPath(project.slug, list.id)}>{list.name}</Link>
                    </Table.RowHeaderCell>
                    <Table.Cell>
                      {list.description ?? (
                        <span style={{ color: "var(--gray-9)" }}>No description</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant="soft">
                        {list.count_customers}{" "}
                        {list.count_customers === 1 ? "customer" : "customers"}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {new Date(list.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        size="1"
                        variant="soft"
                        color="red"
                        onClick={() => handleDeleteList(list.id, list.name)}
                      >
                        <Trash2 className="mr-1 h-3 w-3" />
                        Delete
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card>
      </Flex>
    </ProjectLayout>
  )
}
